import { useQuery } from "@apollo/client";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  UserGroupIcon,
} from "@heroicons/react/outline";
import { uniq } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { RiCollapseDiagonalLine, RiExpandDiagonalLine } from "react-icons/ri";
import { useLocation } from "react-router-dom";
import {
  GetOrgChartOrganizationMemberQueryQuery,
  GetOrgChartOrganizationMemberQueryQueryVariables,
  OrgChartUserNodeFragment,
  UserStatus,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import MemberEditDialog from "@apps/org-settings/components/member-edit-dialog";
import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import AppLabel from "@components/draft-label/app-label";
import Dropdown from "@components/dropdown/dropdown";
import Layout from "@components/layout/layout";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxOption,
  UserComboboxOptionType,
  UserComboboxTeamOption,
  UserComboboxUserOption,
} from "@components/user-combobox/user-combobox-list";
import { classNames } from "@helpers/css";
import { getUrlUsingTypename } from "@helpers/helpers";
import useUrlQueryParams from "@helpers/hooks/use-url-query-params";

import getOrgChartOrganizationMemberQuery from "./graphql/get-org-chart-organization-member-query";
import useFetchOrgChart from "./use-fetch-org-chart";

type OrgChartUserNodeFragmentWithManagerIdsAndReportIds =
  OrgChartUserNodeFragment & {
    managerId?: number;
    reportIds: number[];
    teamIds: number[];
  };

const getAncestorIds = (
  userId: number,
  usersById: Record<number, OrgChartUserNodeFragmentWithManagerIdsAndReportIds>,
): number[] => {
  const user = usersById[userId];
  if (!user) {
    return [];
  }
  if (user.managerId === undefined) {
    return [user.id];
  }
  const ancestorIds = getAncestorIds(user.managerId, usersById);
  return [user.id, ...ancestorIds];
};

const OrgChartUserItem = ({
  user,
  usersById,
  onToggleNode,
  expandedNodes,
  highlightedIds,
  filteredUserIds,
}: {
  user: OrgChartUserNodeFragmentWithManagerIdsAndReportIds;
  usersById: Record<number, OrgChartUserNodeFragmentWithManagerIdsAndReportIds>;
  onToggleNode: (userId: number) => void;
  expandedNodes: Record<number, boolean>;
  highlightedIds: number[];
  filteredUserIds: number[];
}) => {
  const isAdmin = isAdminVar();
  const currentOrganization = currentOrganizationVar();
  const [isEditing, setIsEditing] = useState(false);
  const location = useLocation<TFLocationState>();
  const isExpanded = !!expandedNodes[user.id];
  const reports = user.reportIds
    .filter((id) => filteredUserIds.includes(id))
    .map((id) => usersById[id]);
  return (
    <div className="mt-3 relative">
      <div className="flex items-center gap-2">
        <div
          className={classNames(
            "flex-1 flex gap-2 px-4 py-2 border rounded-md items-center ml-8 relative shadow-sm",
            highlightedIds.includes(user.id) && "bg-yellow-50",
          )}
        >
          {reports.length > 0 && (
            <button
              className="absolute -left-6 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-gray-100"
              onClick={() => onToggleNode(user.id)}
            >
              {isExpanded ? (
                <ChevronDownIcon className="w-3 h-3 text-gray-500" />
              ) : (
                <ChevronRightIcon className="w-3 h-3 text-gray-500" />
              )}
            </button>
          )}
          <Avatar user={user} size="5" />
          <AppLink
            className={classNames(
              "flex-1 text-sm text-gray-800 hover:underline flex items-center gap-2",
              user.status === UserStatus.Inactive && "text-gray-400",
            )}
            to={getUrlUsingTypename(user, location)}
          >
            {user.name}
            {user.status === UserStatus.Inactive && (
              <span>
                <AppLabel label="Inactive" />
              </span>
            )}
          </AppLink>
          {user.currentRoles.length > 0 && (
            <div className="text-gray-400 text-xs">
              {user.currentRoles.map((role) => role.title).join(", ")}
            </div>
          )}
          <div className="ml-2 flex items-center gap-1 w-[52px] justify-center text-xs text-gray-500 rounded-md bg-gray-100 px-2 py-1">
            <UserGroupIcon className="w-4 h-4 shrink-0" />
            {user.reportIds.length}
          </div>
        </div>
        <div className="w-7 flex items-center justify-center">
          {isAdmin && (
            <Dropdown
              options={[{ label: "Edit", onClick: () => setIsEditing(true) }]}
              aria-label="Edit user"
              align="end"
            />
          )}
          {isEditing && (
            <MemberEditDialog
              memberId={user.id}
              organization={currentOrganization}
              onClose={() => setIsEditing(false)}
            />
          )}
        </div>
      </div>
      {isExpanded && reports.length > 0 && (
        <div className="relative">
          {isExpanded && (
            <div className="absolute left-[42px] top-0 bottom-0 w-px bg-gray-100" />
          )}
          <OrgChartUserList
            className="ml-6"
            users={reports}
            usersById={usersById}
            onToggleNode={onToggleNode}
            expandedNodes={expandedNodes}
            highlightedIds={highlightedIds}
            filteredUserIds={filteredUserIds}
          />
        </div>
      )}
    </div>
  );
};

const OrgChartUserList = ({
  users,
  usersById,
  className,
  onToggleNode,
  expandedNodes,
  highlightedIds,
  filteredUserIds,
}: {
  users: OrgChartUserNodeFragmentWithManagerIdsAndReportIds[];
  usersById: Record<number, OrgChartUserNodeFragmentWithManagerIdsAndReportIds>;
  className?: string;
  onToggleNode: (userId: number) => void;
  expandedNodes: Record<number, boolean>;
  highlightedIds: number[];
  filteredUserIds: number[];
}) => {
  return (
    <div className={className}>
      {users.map((user) => (
        <OrgChartUserItem
          key={user.id}
          user={user}
          usersById={usersById}
          onToggleNode={onToggleNode}
          expandedNodes={expandedNodes}
          highlightedIds={highlightedIds}
          filteredUserIds={filteredUserIds}
        />
      ))}
    </div>
  );
};

const OrgChart = () => {
  const label = useLabel();
  const currentOrganization = currentOrganizationVar();
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const [expandedNodes, setExpandedNodes] = useState<Record<
    number,
    boolean
  > | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<
    UserComboboxUserOption | UserComboboxTeamOption | null
  >(null);
  const { userId } = useUrlQueryParams<{
    userId: number;
  }>({
    userId: 0,
  });

  useQuery<
    GetOrgChartOrganizationMemberQueryQuery,
    GetOrgChartOrganizationMemberQueryQueryVariables
  >(getOrgChartOrganizationMemberQuery, {
    skip: Number(userId) <= 0,
    variables: {
      organizationId: currentOrganization.id,
      userId: Number(userId) || -1,
    },

    onCompleted: (data) => {
      const member = data.organization?.members.edges[0]?.node;
      if (member?.user) {
        setSelectedFilter({
          id: member.user.id,
          type: UserComboboxOptionType.USER,
          name: member.user.name,
        });
      }
    },
  });

  const handleToggleNode = (nodeId: number) => {
    const expanded = expandedNodes ? !expandedNodes[nodeId] : true;
    setExpandedNodes((prev) => ({
      ...(prev || {}),
      [nodeId]: expanded,
    }));
  };

  const handleClickExpandAll = () => {
    setExpandedNodes(
      users.reduce(
        (acc, user) => ({
          ...acc,
          [user.id]: true,
        }),
        {},
      ),
    );
  };
  const handleClickCollapseAll = () => {
    setExpandedNodes(
      users.reduce(
        (acc, user) => ({
          ...acc,
          [user.id]: false,
        }),
        {},
      ),
    );
  };

  const { members, loading } = useFetchOrgChart();

  const users: OrgChartUserNodeFragmentWithManagerIdsAndReportIds[] =
    useMemo(() => {
      const tempUsers = members
        .map((member) => member.user)
        .filter((user) => user !== null && user !== undefined)
        .map((user) => ({
          ...user,
          managerId:
            user.primaryManager?.id || user.managers.edges[0]?.node?.id,
          teamIds: user.teamsList.map((team) => team.id),
        }));

      return tempUsers.map((user) => {
        return {
          ...user,
          reportIds: tempUsers
            .filter((u) => u.managerId === user.id)
            .map((u) => u.id),
        };
      });
    }, [members]);

  const usersById = useMemo(() => {
    return users.reduce(
      (acc, user) => {
        acc[user.id] = user;
        return acc;
      },
      {} as Record<number, OrgChartUserNodeFragmentWithManagerIdsAndReportIds>,
    );
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (!selectedFilter) {
      return users;
    }
    if (selectedFilter.type === UserComboboxOptionType.USER) {
      const ancestorIds = getAncestorIds(selectedFilter.id, usersById);
      return users.filter((user) => ancestorIds.includes(user.id));
    } else if (selectedFilter.type === UserComboboxOptionType.TEAM) {
      const teamUsers = users.filter((user) =>
        user.teamIds.includes(selectedFilter.id),
      );
      const ancestorIdOfTeamMembers = uniq(
        teamUsers.reduce((memo, user) => {
          return [...memo, ...getAncestorIds(user.id, usersById)];
        }, [] as number[]),
      );
      return users.filter((user) => ancestorIdOfTeamMembers.includes(user.id));
    }
    return users;
  }, [users, selectedFilter, usersById]);

  const filteredUserIds = useMemo(() => {
    return filteredUsers.map((user) => user.id);
  }, [filteredUsers]);

  const usersWithoutManagers = useMemo(() => {
    return users.filter((user) => !user.managerId);
  }, [users]);

  const highlightedIds = useMemo(() => {
    if (!selectedFilter) {
      return [];
    }
    if (selectedFilter.type === UserComboboxOptionType.USER) {
      return [selectedFilter.id];
    } else if (selectedFilter.type === UserComboboxOptionType.TEAM) {
      return users
        .filter((user) => user.teamIds.includes(selectedFilter.id))
        .map((user) => user.id);
    }
    return [];
  }, [selectedFilter, users]);

  const {
    options,
    loading: loadingUserCombobox,
    query,
    setQuery,
  } = useUserComboboxQuery({
    types: [UserComboboxOptionType.USER, UserComboboxOptionType.TEAM],
  });

  const handleSelectedFilter = (option: UserComboboxOption | null) => {
    if (
      option?.type === UserComboboxOptionType.USER ||
      option?.type === UserComboboxOptionType.TEAM
    ) {
      setSelectedFilter(option);
    } else {
      setSelectedFilter(null);
    }
  };

  const expandedNodesIsNull = expandedNodes === null && !loading;
  useEffect(() => {
    if (selectedFilter) {
      const newExpandedNodes = filteredUserIds.reduce(
        (memo, userId) => ({
          ...memo,
          [userId]: true,
        }),
        {} as Record<number, boolean>,
      );
      setExpandedNodes(newExpandedNodes);
    } else if (expandedNodesIsNull) {
      setExpandedNodes(
        users.reduce(
          (memo, user) => ({
            ...memo,
            [user.id]: !user.managerId,
          }),
          {},
        ),
      );
    }
  }, [users, filteredUserIds, selectedFilter, expandedNodesIsNull]);

  return (
    <Layout>
      <Layout.Header
        breadcrumbs={[
          {
            title: "Org Chart",
            url: "/org-chart",
          },
        ]}
      />
      <Layout.Container>
        <Layout.Main width="xl">
          <div>
            <div className="flex justify-between gap-2">
              <div className="flex gap-6 ml-8 items-center ">
                <UserCombobox
                  value={selectedFilter}
                  options={options}
                  query={query}
                  onChangeValue={handleSelectedFilter}
                  onChangeQuery={setQuery}
                  loading={loadingUserCombobox}
                  placeholder={`Filter by user & ${label("team")}...`}
                  onClearValue={() => setSelectedFilter(null)}
                  clearable
                />
                <label className="flex items-center gap-1 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={
                      uiPreferenceCache.orgChartShowInactiveUsers === true
                    }
                    onChange={(e) =>
                      saveUiPreference({
                        orgChartShowInactiveUsers: e.target.checked,
                      })
                    }
                  />
                  <span>Show inactive users</span>
                </label>
              </div>
              <div className="flex gap-2 mr-9">
                <Button theme={buttonTheme.text} onClick={handleClickExpandAll}>
                  <RiExpandDiagonalLine className="w-5 h-5" />
                  Expand All
                </Button>
                <Button
                  theme={buttonTheme.text}
                  onClick={handleClickCollapseAll}
                >
                  <RiCollapseDiagonalLine className="w-5 h-5" />
                  Collapse All
                </Button>
              </div>
            </div>
            <div>
              {loading ? (
                <Loading size="5" className="mt-4">
                  Loading org chart...
                </Loading>
              ) : usersWithoutManagers.length === 0 ? (
                <div className="text-gray-500 text-sm">No users found</div>
              ) : (
                <OrgChartUserList
                  users={usersWithoutManagers}
                  usersById={usersById}
                  onToggleNode={handleToggleNode}
                  expandedNodes={expandedNodes || {}}
                  highlightedIds={highlightedIds}
                  filteredUserIds={filteredUserIds}
                />
              )}
            </div>
          </div>
        </Layout.Main>
      </Layout.Container>
    </Layout>
  );
};

export default OrgChart;
